// Provisioner side of Mesh provisioning (Mesh Profile 5.4), CMAC/P-256 only.
// Transport-agnostic: give it send/next for provisioning PDUs.
//
// Ported from the cob-mesh-reactjs sampler (src/mesh/provisioning.ts). One
// change: `unicast` may be an ASYNC allocator. Here the address comes from the
// server (AllocateMeshAddress), and the element count it needs is only known
// once the device has sent its Capabilities — so the allocation happens
// mid-protocol, between Capabilities and Start. The device waits; the Mesh
// link timeout is far longer than one REST round trip.

import {
  aesCmac, ccmEncrypt, ecdhKeyPair, ecdhSecret, k1, s1,
} from './meshCrypto';
import {
  ascii, concat, equal, hex, randomBytes, readU16be, u16be, u32be,
} from './bytes';

/* eslint-disable no-bitwise */

export const PROV = {
  INVITE: 0, CAPS: 1, START: 2, PUBKEY: 3, INPUT_COMPLETE: 4, CONFIRM: 5, RANDOM: 6, DATA: 7, COMPLETE: 8, FAILED: 9,
};

const FAIL_REASONS = {
  1: 'Invalid PDU',
  2: 'Invalid format',
  3: 'Unexpected PDU',
  4: 'Confirmation failed (wrong auth/OOB value)',
  5: 'Out of resources',
  6: 'Decryption failed',
  7: 'Unexpected error',
  8: 'Cannot assign addresses',
  9: 'Invalid data',
};

export const parseCapabilities = (p) => ({
  raw: p,
  numElements: p[0],
  algorithms: readU16be(p, 1),
  publicKeyType: p[3],
  staticOOBType: p[4],
  outputOOBSize: p[5],
  outputOOBAction: readU16be(p, 6),
  inputOOBSize: p[8],
  inputOOBAction: readU16be(p, 9),
});

const pdu = (type, params = new Uint8Array(0)) => concat(Uint8Array.of(type), params);

/**
 * io: { send(pdu), next(timeoutMs), log(text) }
 * o:  { netKey, netKeyIndex, ivIndex, unicast: number | (numElements) => number|Promise<number>, staticOOB? }
 * -> { devKey, unicast, numElements, caps }
 */
export async function provision(io, o) {
  const expect = async (type, timeout = 15000) => {
    const r = await io.next(timeout);
    const t = r[0] & 0x3f;
    if (t === PROV.FAILED) throw new Error(`The lamp refused pairing: ${FAIL_REASONS[r[1]] ?? `code ${r[1]}`}`);
    if (t !== type) throw new Error(`Unexpected provisioning PDU type ${t} (wanted ${type})`);
    return r.slice(1);
  };

  // 1. Invite — the attention timer makes the lamp blink for 5 s.
  const invite = Uint8Array.of(5);
  io.log('Invite sent (the lamp may blink)');
  await io.send(pdu(PROV.INVITE, invite));

  // 2. Capabilities
  const capsRaw = await expect(PROV.CAPS);
  const caps = parseCapabilities(capsRaw);
  io.log(`Capabilities: ${caps.numElements} element(s), static OOB ${caps.staticOOBType & 1 ? 'yes' : 'no'}`);
  if (!(caps.algorithms & 1)) throw new Error('This lamp does not support the CMAC/P-256 pairing algorithm.');

  // The address range is reserved now that the element count is known.
  const unicast = typeof o.unicast === 'function' ? await o.unicast(caps.numElements) : o.unicast;

  // 3. Authentication: static OOB when the lamp wants it AND we have a key, else No OOB.
  let authMethod = 0x00;
  let authValue = new Uint8Array(16);
  if (o.staticOOB && (caps.staticOOBType & 1)) {
    authMethod = 0x01;
    authValue = o.staticOOB;
  }
  const start = Uint8Array.of(0x00, 0x00, authMethod, 0x00, 0x00);
  await io.send(pdu(PROV.START, start));

  // 4. Public keys
  const kp = ecdhKeyPair();
  await io.send(pdu(PROV.PUBKEY, kp.publicKey));
  const devPub = await expect(PROV.PUBKEY);
  let secret;
  try {
    secret = ecdhSecret(kp.secretKey, devPub);
  } catch (e) {
    throw new Error('The lamp sent an invalid public key');
  }

  // 5. Confirmation
  const confInputs = concat(invite, capsRaw, start, kp.publicKey, devPub);
  const confSalt = s1(confInputs);
  const confKey = k1(secret, confSalt, ascii('prck'));
  const randProv = randomBytes(16);
  const confProv = aesCmac(confKey, concat(randProv, authValue));
  await io.send(pdu(PROV.CONFIRM, confProv));
  const confDev = await expect(PROV.CONFIRM);
  if (equal(confDev, confProv)) throw new Error('The lamp echoed our confirmation (reflection guard)');

  // 6. Random exchange + verify
  await io.send(pdu(PROV.RANDOM, randProv));
  const randDev = await expect(PROV.RANDOM);
  const check = aesCmac(confKey, concat(randDev, authValue));
  if (!equal(check, confDev)) {
    throw new Error('Confirmation mismatch: this lamp expects a vendor pairing key.');
  }
  io.log('Confirmation verified');

  // 7. Session keys + provisioning data
  const provSalt = s1(concat(confSalt, randProv, randDev));
  const sessionKey = k1(secret, provSalt, ascii('prsk'));
  const sessionNonce = k1(secret, provSalt, ascii('prsn')).slice(3);
  const devKey = k1(secret, provSalt, ascii('prdk'));
  const data = concat(o.netKey, u16be(o.netKeyIndex), Uint8Array.of(0x00), u32be(o.ivIndex), u16be(unicast));
  io.log(`Provisioning data sent (address 0x${unicast.toString(16).padStart(4, '0')})`);
  await io.send(pdu(PROV.DATA, ccmEncrypt(sessionKey, sessionNonce, data, 8)));
  await expect(PROV.COMPLETE, 20000);
  io.log(`Paired, device key ${hex(devKey).slice(0, 8)}…`);
  return {
    devKey, unicast, numElements: caps.numElements, caps,
  };
}

// ----- Device side — used only by the tests, to pair against a simulated lamp -----
export async function simulateDevice(io, staticOOB) {
  const { ccmDecrypt } = await import('./meshCrypto');
  const take = async (t) => {
    const r = await io.next();
    if (r[0] !== t) throw new Error(`sim: unexpected ${r[0]}`);
    return r.slice(1);
  };
  const invite = await take(PROV.INVITE);
  const caps = Uint8Array.of(1, 0, 1, 0, staticOOB ? 1 : 0, 0, 0, 0, 0, 0, 0);
  await io.send(pdu(PROV.CAPS, caps));
  const start = await take(PROV.START);
  const provPub = await take(PROV.PUBKEY);
  const kp = ecdhKeyPair();
  await io.send(pdu(PROV.PUBKEY, kp.publicKey));
  const secret = ecdhSecret(kp.secretKey, provPub);
  const auth = staticOOB ?? new Uint8Array(16);
  const confSalt = s1(concat(invite, caps, start, provPub, kp.publicKey));
  const confKey = k1(secret, confSalt, ascii('prck'));
  const confProv = await take(PROV.CONFIRM);
  const randDev = randomBytes(16);
  await io.send(pdu(PROV.CONFIRM, aesCmac(confKey, concat(randDev, auth))));
  const randProv = await take(PROV.RANDOM);
  if (!equal(aesCmac(confKey, concat(randProv, auth)), confProv)) {
    await io.send(Uint8Array.of(PROV.FAILED, 4));
    return null;
  }
  await io.send(pdu(PROV.RANDOM, randDev));
  const provSalt = s1(concat(confSalt, randProv, randDev));
  const enc = await take(PROV.DATA);
  const data = ccmDecrypt(k1(secret, provSalt, ascii('prsk')), k1(secret, provSalt, ascii('prsn')).slice(3), enc, 8);
  await io.send(pdu(PROV.COMPLETE));
  return { data, devKey: k1(secret, provSalt, ascii('prdk')) };
}
